import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Contact from '../components/Contact';

const options = [
  {
    title: 'Start a Project',
    desc: 'You have something to build. An AI agent, a product, an automation, or a data setup. Tell us about it and we will tell you honestly how we would approach it.',
    topic: 'Software Development',
    link: { to: '/services', label: 'See our services' },
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        <line x1="12" y1="2" x2="12" y2="22"/>
      </svg>
    ),
  },
  {
    title: 'Hire Developers',
    desc: 'You need Python, AWS, Docker, or system design developers working with your team. Full-time, part-time, or by the hour.',
    topic: 'Hire Developers',
    link: { to: '/hire-developers', label: 'How hiring works' },
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    title: 'Ask a Question',
    desc: 'Not sure yet? Want a second opinion on your tech, or just curious what is possible? Ask away. No sales pitch.',
    topic: 'Something else',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
];

const nextSteps = [
  {
    num: '01',
    title: 'We read your message',
    desc: 'A real person on our team reads it, not a bot. If anything is unclear, we will ask before assuming.',
  },
  {
    num: '02',
    title: 'We set up a call',
    desc: 'A short call to understand what you need, what you have today, and what a good result looks like for you.',
  },
  {
    num: '03',
    title: 'You get a clear plan',
    desc: 'We come back with how we would do it, who would work on it, and what it would take. Then you decide.',
  },
];

const ContactPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const [preset, setPreset] = useState<{ value: string; at: number }>();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    pageRef.current?.querySelectorAll('.contact-group').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const pick = (topic: string) => {
    setPreset({ value: topic, at: Date.now() });
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="svcpage" ref={pageRef}>
      {/* Page hero */}
      <div className="svcpage-hero">
        <div className="orb orb-1" style={{ opacity: 0.5 }} />
        <div className="orb orb-2" style={{ opacity: 0.4 }} />
        <div className="hero-grid" />
        <div className="container">
          <span className="section-label" style={{ justifyContent: 'center', display: 'flex', marginBottom: 16 }}>
            Get In Touch
          </span>
          <h1>
            Tell us what<br />
            <span className="gt">you are working on</span>
          </h1>
          <p>
            A new project, extra developers for your team, or just a question.
            Send us a message or give us a call. You will hear back from the
            people who would actually do the work.
          </p>
          <div className="hire-hero-ctas">
            <a href="mailto:team@slatewing.com" className="btn btn-primary partner-btn">
              team@slatewing.com
            </a>
            <a href="tel:+917359005500" className="btn btn-ghost">
              +91 73590 05500
            </a>
          </div>
        </div>
      </div>

      {/* What can we help with */}
      <section className="hire-section contact-group" style={{ paddingTop: 40 }}>
        <div className="container">
          <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 56px' }}>
            <span className="section-label" style={{ justifyContent: 'center' }}>How Can We Help</span>
            <h2>What brings<br /><span className="gt">you here?</span></h2>
          </div>

          <div className="whyus-grid">
            {options.map((o, i) => (
              <div className={`whyus-card getintouch-card reveal d${i + 1}`} key={o.title}>
                <div className="whyus-icon">{o.icon}</div>
                <h3 className="whyus-title">{o.title}</h3>
                <p className="whyus-desc">{o.desc}</p>
                <div className="getintouch-actions">
                  <button className="hire-card-link" onClick={() => pick(o.topic)}>
                    Send a message
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                  {o.link && (
                    <Link to={o.link.to} className="getintouch-sublink">{o.link.label}</Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      <Contact presetTopic={preset} />

      <div className="section-divider" />

      {/* What happens next */}
      <section className="hire-section contact-group">
        <div className="container">
          <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 56px' }}>
            <span className="section-label" style={{ justifyContent: 'center' }}>What Happens Next</span>
            <h2>After you<br /><span className="gt">reach out</span></h2>
          </div>

          <div className="whyus-grid">
            {nextSteps.map((s, i) => (
              <div className={`process-card reveal d${i + 1}`} key={s.num}>
                <div className="process-num-bg">{s.num}</div>
                <div className="process-top">
                  <span className="process-badge">{s.num}</span>
                </div>
                <h3 className="process-title">{s.title}</h3>
                <p className="process-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
